import { useCallback, useEffect, useMemo, useState } from 'react';

import { createColumnHelper, getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { useParams, useSearchParams } from 'react-router-dom';

import { FileDeletionType } from '../../../../../../../../../backend/src/types/types';
import { FileHistoryLog, LogEvent, UploadLogEvent } from '../components/file-history-log';
import { DeletionDetailModal } from '../components/modals/deletion-detail-modal';
import { UploadDetailModal } from '../components/modals/upload-detail-modal';
import { formatNumber } from '../utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDeletedFiles } from '@/api/queries/useGetDeletedFiles';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { DeletionHistory, UploadHistory } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';

function buildLogEvents(uploads: UploadHistory[] | undefined, deletions: DeletionHistory[] | undefined): LogEvent[] {
  const events: LogEvent[] = [];

  // Add upload events
  uploads?.forEach((upload) => {
    const attempted = (upload.successfullyUploadedFileCount ?? 0) + (upload.uploadFailures?.length ?? 0);
    events.push({
      id: upload.id,
      type: 'upload',
      date: upload.createdAt as unknown as string, // Date type in entity, string from JSON
      userEmail: upload.createdBy?.email ?? 'Unknown',
      status: upload.status,
      attempted,
      successful: upload.successfullyUploadedFileCount ?? 0,
      skipped: upload.uploadFailures?.length ?? 0,
    });
  });

  // Add deletion events
  deletions?.forEach((deletion) => {
    events.push({
      id: deletion.id,
      type: 'deletion',
      date: deletion.createdAt,
      userEmail: deletion.createdBy?.email ?? 'Unknown',
      count: deletion.fileDeletion?.length ?? 0,
      deletionType: deletion.type,
    });
  });

  // Sort by date descending (newest first)
  events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return events;
}

const columnHelper = createColumnHelper<LogEvent>();

function getUploadStatusChip(status: UploadHistory['status']) {
  if (status === 'failed') {
    return <Chip className="bg-red-50 text-red-700 ring-1 ring-red-200 ring-inset">Failed</Chip>;
  }

  if (status === 'cancelled') {
    return <Chip className="bg-amber-50 text-amber-700 ring-1 ring-amber-200 ring-inset">Cancelled</Chip>;
  }

  if (status === 'processing') {
    return <Chip className="bg-blue-50 text-blue-700 ring-1 ring-blue-200 ring-inset">Processing</Chip>;
  }

  return <Chip className="bg-green-50 text-green-700 ring-1 ring-green-200 ring-inset">Complete</Chip>;
}

const columns = [
  columnHelper.accessor('date', {
    header: () => <span>Date</span>,
    cell: (info) => (
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <Icon className="lucide--clock h-3 w-3" />
        <span>{formatDate(info.getValue(), true, true)}</span>
      </div>
    ),
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => {
      const type = info.getValue();
      return (
        <div className="flex items-center gap-2">
          <Icon className={`size-3 text-gray-800 ${type === 'upload' ? 'lucide--upload' : 'lucide--trash-2'}`} />
          <span className="text-xs text-gray-800">{type === 'upload' ? 'Files Uploaded' : 'Files Deleted'}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('userEmail', {
    header: () => <span>User</span>,
    cell: (info) => <span className="text-xs text-gray-800">{info.getValue()}</span>,
  }),
  columnHelper.display({
    id: 'status',
    header: () => <span>Status</span>,
    cell: (info) => {
      const event = info.row.original;
      if (event.type !== 'upload') {
        return <span className="text-xs text-gray-400">-</span>;
      }

      return getUploadStatusChip(event.status);
    },
  }),
  columnHelper.display({
    id: 'summary',
    header: () => <span>Summary</span>,
    cell: (info) => {
      const event = info.row.original;
      if (event.type === 'upload') {
        const upload = event as UploadLogEvent;
        return (
          <div className="flex items-center gap-2 text-xs">
            <div className="flex items-center gap-1.5 rounded-md bg-blue-50 px-2 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              <span className="text-blue-700">{formatNumber(upload.attempted)} attempted</span>
            </div>
            <Icon className="lucide--arrow-right h-3 w-3 text-gray-300" />
            <div className="flex items-center overflow-hidden rounded-md">
              <div className="flex items-center gap-1.5 bg-green-50 px-2 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                <span className="text-green-700">{formatNumber(upload.successful)} uploaded</span>
              </div>
              <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                <span className="text-amber-700">{formatNumber(upload.skipped)} skipped</span>
              </div>
            </div>
          </div>
        );
      }
      // Deletion event
      return (
        <div className="flex items-center overflow-hidden text-xs">
          <div className="flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-red-600" />
            <span className="text-red-700">
              {formatNumber(event.count)} deleted{' '}
              {event.deletionType === FileDeletionType.Staged ? 'before submission' : 'after submission'}
            </span>
          </div>
        </div>
      );
    },
  }),
];

export function FileLog() {
  const { matterId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  if (!matterId) return null;

  const { data: uploads, isLoading } = useGetMatterUploadHistory(matterId);
  const { data: deletions, isLoading: isLoadingDeletions } = useGetDeletedFiles(matterId);

  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [selectedDeletionId, setSelectedDeletionId] = useState<string | null>(null);

  // Auto-open upload detail modal from ?upload=<id> query param
  useEffect(() => {
    const uploadParam = searchParams.get('upload');
    if (uploadParam) {
      setSelectedUploadId(uploadParam);
      searchParams.delete('upload');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const logEvents = useMemo(() => buildLogEvents(uploads, deletions), [uploads, deletions]);
  const isLogLoading = isLoading || isLoadingDeletions;

  const logTable = useReactTable({
    data: logEvents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  const handleEventClick = useCallback(
    (event: LogEvent) => {
      if (event.type === 'upload') {
        trackCustomEvent(TrackingEventsV2.FileHistoryClickUploadEvent, { matterId, eventId: event.id });
        setSelectedUploadId(event.id);
      } else {
        trackCustomEvent(TrackingEventsV2.FileHistoryClickDeletionEvent, { matterId, eventId: event.id });
        setSelectedDeletionId(event.id);
      }
    },
    [matterId],
  );

  return (
    <>
      <FileHistoryLog
        isLogLoading={isLogLoading}
        logEvents={logEvents}
        logTable={logTable}
        onEventClick={handleEventClick}
      />
      {selectedUploadId && (
        <UploadDetailModal
          open={!!selectedUploadId}
          onOpenChange={(open) => !open && setSelectedUploadId(null)}
          matterId={matterId}
          uploadId={selectedUploadId}
        />
      )}
      {selectedDeletionId && (
        <DeletionDetailModal
          open={!!selectedDeletionId}
          onOpenChange={(open) => !open && setSelectedDeletionId(null)}
          matterId={matterId}
          deletionId={selectedDeletionId}
        />
      )}
    </>
  );
}
